// Restaurante — mesas, cocina, delivery, recetas/ingredientes y modo offline
// (agosto 2026).
//
// Construido sobre el motor de POS de mostrador de la Fase 10: cada comanda
// se cierra facturando contra POST /pos/ventas... por dentro, así que el
// impacto en inventario/contabilidad es exactamente el de una venta de
// mostrador normal (ver app/restaurante_utils.py). Cuatro vistas dentro de
// la misma pantalla: "Mesas" (plano de mesas + editor visual + gestión de
// cada comanda), "Cocina" (tablero de ítems con tiempos en vivo), "Delivery"
// (pedidos a domicilio, sin mesa real) e "Ingredientes" (ficha técnica de
// cada plato y alertas de bajo stock).
//
// Los pedidos a domicilio son comandas normales con tipo='delivery' — por
// eso ModalComanda se reutiliza sin duplicar lógica de cobro/anulación.
//
// Cola offline: ver src/offline.ts para el alcance exacto — incluye abrir una
// mesa NUEVA sin conexión (con un ID temporal que se resuelve al reconectar)
// además de las acciones sobre una comanda YA ABIERTA. Aquí se usa a través
// de los wrappers `*Offline` en vez de llamar a `api` directamente.
import React, { useEffect, useState, useRef } from "react";
import { Shell } from "../components/Shell";
import { api, ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";
import { Icon } from "../icons";
import { money, MEDIO_PAGO_LABEL } from "./Pos";
import {
  AccionCola, suscribirCola, suscribirSincronizacion, sincronizar, listarColaDeComanda,
  agregarItemOffline, quitarItemOffline, cambiarEstadoItemOffline, enviarACocinaOffline, reenviarACocinaOffline, cerrarComandaOffline,
  moverCuentaOffline, cobrarCuentaOffline,
  abrirComandaOffline, descartarComandaTemporal, esIdTemporal,
  guardarCacheComanda, leerCacheComanda, guardarCacheProductos, leerCacheProductos,
  guardarCacheTurnos, leerCacheTurnos, guardarCacheClientes, leerCacheClientes, guardarCacheMeseros, leerCacheMeseros,
} from "../offline";

// ------------------------------------------------------------------- tipos
type Mesa = {
  id: number; numero: string; zona: string | null; capacidad: number | null; estado: string;
  salonId: number | null; posX: number | null; posY: number | null;
  comandaAbierta?: { id: number; numero: string; estado: string; numComensales: number | null; fechaApertura: string; total: number; meseroNombre: string | null } | null;
  // Unificación de mesas (grupos grandes, ver Tarea de "unificar mesas"):
  // una satélite trae mesaPrincipalId apuntando a su principal; la
  // principal trae mesasVinculadas con la lista de sus satélites. Nunca
  // ambos a la vez en la misma mesa.
  mesaPrincipalId?: number | null;
  mesasVinculadas?: { id: number; numero: string }[];
};
type Salon = { id: number; nombre: string; orden: number; creadoEn: string };
type ItemComanda = {
  id: number; inventarioPtId: number; producto: string; cantidad: number; precio: number;
  observaciones: string | null; estadoCocina: string; creadoEn: string; chips?: string[];
  enviadoCocina?: boolean; cuenta?: number; pagado?: boolean;
};
type Comanda = {
  id: number; numero: string; mesaId: number; mesaNumero: string; turnoCajaId: number | null;
  estado: string; numComensales: number | null; observaciones: string | null; ventaPosId: number | null;
  usuarioAperturaNombre: string | null; meseroId: number | null; meseroNombre: string | null;
  tipo: "mesa" | "delivery" | "mostrador"; estadoDelivery: string | null;
  clienteId: number | null; clienteNombre: string | null; telefonoEntrega: string | null; direccionEntrega: string | null;
  fechaApertura: string; fechaCierre: string | null;
  items?: ItemComanda[]; total?: number;
};
type ItemCocina = ItemComanda & { comandaId: number; comandaNumero: string; mesaNumero: string };
type ProductoDisponible = { id: number; producto: string; cantidadDisponible: number; precioSugerido: number | null; unidadVenta: string };
type Turno = { id: number; caja: string; estado: string };
type Cliente = { id: number; nombre: string; nit: string | null; email: string | null };
type Mesero = { id: number; nombre: string };
type Ingrediente = {
  id: number; nombre: string; unidadMedida: string; cantidadDisponible: number; stockMinimo: number;
  costoUnitario: number; actualizadoEn: string; bajoStock: boolean;
};
type Receta = { id: number; producto: string; actualizadoEn: string; ingredientes: { id: number; ingredienteId: number; ingredienteNombre: string; unidadMedida: string; cantidad: number }[] };
type PedidoDelivery = {
  id: number; numero: string; estado: string; estadoDelivery: string;
  clienteId: number | null; clienteNombre: string | null; telefonoEntrega: string | null; direccionEntrega: string | null;
  observaciones: string | null; usuarioAperturaNombre: string | null; fechaApertura: string;
  items: ItemComanda[]; total: number;
};

const ESTADO_MESA_ESTILO: Record<string, string> = {
  libre: "bg-emerald-50 border-emerald-200 text-emerald-700",
  ocupada: "bg-accent-light border-accent/40 text-accent-dark",
  reservada: "bg-blue-50 border-blue-200 text-blue-700",
  por_limpiar: "bg-amber-50 border-amber-200 text-amber-700",
};
const ESTADO_MESA_LABEL: Record<string, string> = { libre: "Libre", ocupada: "Ocupada", reservada: "Reservada", por_limpiar: "Por limpiar" };
const ESTADO_COCINA_LABEL: Record<string, string> = { pendiente: "Pendiente", en_preparacion: "En preparación", listo: "Listo", entregado: "Entregado" };
const ESTADO_COCINA_ESTILO: Record<string, string> = {
  pendiente: "bg-slate-100 text-slate-600", en_preparacion: "bg-amber-50 text-amber-700",
  listo: "bg-emerald-50 text-emerald-700", entregado: "bg-slate-50 text-slate-400",
};
const SIGUIENTE_ESTADO_COCINA: Record<string, string | null> = { pendiente: "en_preparacion", en_preparacion: "listo", listo: "entregado", entregado: null };
const ESTADOS_DELIVERY = ["recibido", "en_preparacion", "en_camino", "entregado"] as const;
const ESTADO_DELIVERY_LABEL: Record<string, string> = { recibido: "Recibido", en_preparacion: "En preparación", en_camino: "En camino", entregado: "Entregado" };
const UNIDADES_INGREDIENTE = ["gramos", "mililitros", "unidad"] as const;

// Chips de preparación/orden por ítem — deben coincidir exactamente con
// CHIPS_PREPARACION/CHIPS_ORDEN en restaurante_utils.py (mismos códigos).
const CHIPS_PREPARACION: { valor: string; etiqueta: string }[] = [
  { valor: "sin_sal", etiqueta: "Sin sal" },
  { valor: "sin_picante", etiqueta: "Sin picante" },
  { valor: "bien_cocido", etiqueta: "Bien cocido" },
  { valor: "poca_salsa", etiqueta: "Poca salsa" },
  { valor: "salsa_aparte", etiqueta: "Salsa aparte" },
];
const CHIPS_ORDEN: { valor: string; etiqueta: string }[] = [
  { valor: "para_llevar", etiqueta: "Para llevar" },
  { valor: "primero", etiqueta: "Primero" },
  { valor: "despues", etiqueta: "Después" },
  { valor: "para_compartir", etiqueta: "Para compartir" },
];
const CHIP_ETIQUETA: Record<string, string> = Object.fromEntries(
  [...CHIPS_PREPARACION, ...CHIPS_ORDEN].map((c) => [c.valor, c.etiqueta])
);

// ------------------------------------------------------------- reloj en vivo
// Fuerza un re-render periódico para que los textos de "hace Xm" del tablero
// de cocina y de las mesas ocupadas se mantengan al día sin recargar nada.
function useReloj(intervaloMs: number) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), intervaloMs);
    return () => clearInterval(id);
  }, [intervaloMs]);
}

function transcurrido(fechaIso: string): string {
  // El backend guarda fechas en hora del servidor sin sufijo de zona
  // ("YYYY-MM-DD HH:MM:SS"); Safari no las parsea sin normalizar a ISO.
  const iso = fechaIso.includes("T") ? fechaIso : fechaIso.replace(" ", "T");
  const inicio = new Date(iso.endsWith("Z") ? iso : iso + "Z").getTime();
  if (Number.isNaN(inicio)) return "";
  const ms = Date.now() - inicio;
  const min = Math.max(0, Math.floor(ms / 60000));
  if (min < 1) return "recién";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const rm = min % 60;
  return `${h}h ${rm}m`;
}

function urgenciaCocina(minutos: number): string {
  if (minutos >= 25) return "text-red-600 font-bold";
  if (minutos >= 12) return "text-amber-600 font-semibold";
  return "text-slate-500";
}

function minutosDesde(fechaIso: string): number {
  const iso = fechaIso.includes("T") ? fechaIso : fechaIso.replace(" ", "T");
  const t = new Date(iso.endsWith("Z") ? iso : iso + "Z").getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((Date.now() - t) / 60000));
}

// ------------------------------------------------------------- componente raíz
export function Restaurante() {
  const { tienePermiso } = useAuth();
  const puedeGestionar = tienePermiso("restaurante:gestionar");
  const puedeCocina = tienePermiso("restaurante:cocina");
  const puedeCobrar = tienePermiso("restaurante:cobrar");
  const puedeAnular = tienePermiso("restaurante:anular");
  const puedeRecetas = tienePermiso("restaurante:recetas");

  const [vista, setVista] = useState<"mesas" | "cocina" | "delivery" | "ingredientes">("mesas");
  const [mesas, setMesas] = useState<Mesa[]>([]);
  const [salones, setSalones] = useState<Salon[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [modalNuevaMesa, setModalNuevaMesa] = useState(false);
  const [mesaEditar, setMesaEditar] = useState<Mesa | null>(null);
  const [comandaAbiertaId, setComandaAbiertaId] = useState<number | null>(null);
  // Solo se usa cuando `comandaAbiertaId` es un ID temporal (mesa sentada sin
  // conexión): ModalComanda parte de este objeto en vez de pedirlo por red,
  // porque el servidor todavía no lo conoce.
  const [comandaInicial, setComandaInicial] = useState<Comanda | null>(null);
  const [mesaParaAbrir, setMesaParaAbrir] = useState<Mesa | null>(null);
  const [modalSalones, setModalSalones] = useState(false);
  const [modoEdicionPlano, setModoEdicionPlano] = useState(false);
  const [ingredientesBajoStock, setIngredientesBajoStock] = useState<Ingrediente[]>([]);
  const [pendientesOffline, setPendientesOffline] = useState<AccionCola[]>([]);
  const [enLinea, setEnLinea] = useState(typeof navigator === "undefined" ? true : navigator.onLine);

  const cargarMesas = async () => {
    try {
      setMesas(await api.get("/restaurante/mesas"));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudieron cargar las mesas.");
    }
  };
  const cargarSalones = async () => {
    try {
      setSalones(await api.get("/restaurante/salones"));
    } catch {
      setSalones([]);
    }
  };
  const cargarAlertasStock = async () => {
    if (!puedeRecetas) return;
    try {
      const ings: Ingrediente[] = await api.get("/restaurante/ingredientes");
      setIngredientesBajoStock(ings.filter((i) => i.bajoStock));
    } catch {
      // silencioso: la alerta es informativa, no bloquea el resto de la pantalla
    }
  };

  useEffect(() => {
    cargarMesas();
    cargarSalones();
    cargarAlertasStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsub1 = suscribirCola(setPendientesOffline);
    const unsub2 = suscribirSincronizacion(() => { cargarMesas(); cargarAlertasStock(); });
    const onOnline = () => setEnLinea(true);
    const onOffline = () => setEnLinea(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => { unsub1(); unsub2(); window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clicMesa = (m: Mesa) => {
    if (m.comandaAbierta) {
      setComandaAbiertaId(m.comandaAbierta.id);
    } else if (m.estado === "libre" && puedeGestionar) {
      setMesaParaAbrir(m);
    } else if (m.estado === "por_limpiar" && puedeGestionar) {
      liberar(m.id);
    }
  };

  const liberar = async (mesaId: number) => {
    setError(null);
    try {
      await api.post(`/restaurante/mesas/${mesaId}/liberar`);
      cargarMesas();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo liberar la mesa.");
    }
  };

  const [modoUnificarMesas, setModoUnificarMesas] = useState(false);
  const [abriendoMostrador, setAbriendoMostrador] = useState(false);
  const facturarSinMesa = async () => {
    setError(null);
    setAbriendoMostrador(true);
    try {
      const comanda = await api.post("/restaurante/comandas/mostrador", {});
      setComandaInicial(null);
      setComandaAbiertaId(comanda.id);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo abrir la venta rápida.");
    } finally {
      setAbriendoMostrador(false);
    }
  };

  // Actualización optimista de una mesa puntual cuando se sentó (o se
  // descartó) sin conexión — ver ModalAbrirComanda/ModalComanda. No dispara
  // ningún request: solo refleja en pantalla lo que ya se guardó en la cola
  // local, mientras el servidor todavía no lo sabe.
  const patchMesaAbierta = (mesaId: number, resumen: NonNullable<Mesa["comandaAbierta"]>) => {
    setMesas((prev) => prev.map((m) => (m.id === mesaId ? { ...m, estado: "ocupada", comandaAbierta: resumen } : m)));
  };
  const revertirMesaLibre = (mesaId: number) => {
    setMesas((prev) => prev.map((m) => (m.id === mesaId ? { ...m, estado: "libre", comandaAbierta: null } : m)));
  };

  return (
    <Shell titulo="Restaurante" breadcrumb="Restaurante">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="inline-flex rounded-lg border border-borderc bg-white p-1 flex-wrap">
          {(["mesas", "cocina", "delivery"] as const).map((v) => (
            <button
              key={v} type="button" onClick={() => setVista(v)}
              className={`px-3.5 py-1.5 rounded-md text-xs font-semibold transition-colors capitalize ${vista === v ? "bg-accent text-white" : "text-slate-600 hover:bg-surface"}`}
            >
              {v === "mesas" ? "Mesas" : v === "cocina" ? "Cocina" : "Delivery"}
            </button>
          ))}
          {puedeRecetas && (
            <button
              type="button" onClick={() => setVista("ingredientes")}
              className={`px-3.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${vista === "ingredientes" ? "bg-accent text-white" : "text-slate-600 hover:bg-surface"}`}
            >
              Ingredientes
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <IndicadorOffline enLinea={enLinea} pendientes={pendientesOffline} />
          {puedeGestionar && (
            <button
              type="button"
              className="btn-primary flex items-center gap-1.5 text-xs disabled:opacity-60"
              disabled={abriendoMostrador}
              onClick={facturarSinMesa}
              title="Venta rápida sin asignar mesa (mostrador / para llevar)"
            >
              <Icon name="cart" className="h-4 w-4" /> Facturar Sin Mesa
            </button>
          )}
          {vista === "mesas" && puedeGestionar && (
            <>
              <button
                type="button"
                className={`flex items-center gap-1.5 text-xs rounded-lg border px-3 py-1.5 font-semibold transition-colors ${modoUnificarMesas ? "border-accent bg-accent text-white" : "border-borderc text-slate-600 hover:border-slate-300 bg-white"}`}
                onClick={() => { setModoUnificarMesas((v) => !v); setModoEdicionPlano(false); }}
                title="Vincular 2 o más mesas libres para atender un grupo grande con una sola comanda"
              >
                <Icon name="link" className="h-3.5 w-3.5" /> {modoUnificarMesas ? "Cancelar unión" : "Unificar mesas"}
              </button>
              {!modoUnificarMesas && (
                <>
                  <button type="button" className="btn-secondary flex items-center gap-1.5 text-xs" onClick={() => setModalSalones(true)}>
                    <Icon name="layoutGrid" className="h-3.5 w-3.5" /> Salones
                  </button>
                  <button
                    type="button"
                    className={`flex items-center gap-1.5 text-xs rounded-lg border px-3 py-1.5 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${modoEdicionPlano ? "border-accent bg-accent text-white" : "border-borderc text-slate-600 hover:border-slate-300 bg-white"}`}
                    disabled={salones.length === 0}
                    title={salones.length === 0 ? "Cree al menos un salón antes de editar el plano." : undefined}
                    onClick={() => {
                      if (salones.length === 0) { setError("Cree al menos un salón antes de editar el plano."); return; }
                      setModoEdicionPlano((v) => !v);
                    }}
                  >
                    <Icon name="settings" className="h-3.5 w-3.5" /> {modoEdicionPlano ? "Terminar edición" : "Editar plano"}
                  </button>
                  <button
                    type="button"
                    className="btn-primary flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={salones.length === 0}
                    title={salones.length === 0 ? "Cree al menos un salón antes de agregar una mesa." : undefined}
                    onClick={() => {
                      if (salones.length === 0) { setError("Cree al menos un salón antes de agregar una mesa."); return; }
                      setModalNuevaMesa(true);
                    }}
                  >
                    <Icon name="plus" className="h-4 w-4" /> Nueva mesa
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {ingredientesBajoStock.length > 0 && (
        <div className="mb-4 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800 flex items-start gap-2">
          <Icon name="alertTriangle" className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <span className="font-semibold">Bajo stock de ingredientes:</span>{" "}
            {ingredientesBajoStock.map((i) => i.nombre).join(", ")}.{" "}
            {puedeRecetas && <button type="button" className="underline font-semibold" onClick={() => setVista("ingredientes")}>Ver detalle</button>}
          </div>
        </div>
      )}

      {error && <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}

      {vista === "mesas" && (
        <VistaMesas
          mesas={mesas} salones={salones} onClicMesa={clicMesa} onEditar={setMesaEditar} puedeGestionar={puedeGestionar}
          modoEdicion={modoEdicionPlano} onMoverMesa={cargarMesas} setError={setError}
          modoUnificar={modoUnificarMesas} onSalirUnificar={() => setModoUnificarMesas(false)} onCambioMesas={setMesas}
        />
      )}
      {vista === "cocina" && <VistaCocina puedeCocina={puedeCocina} />}
      {vista === "delivery" && (
        <VistaDelivery puedeGestionar={puedeGestionar} onAbrirComanda={(id) => { setComandaInicial(null); setComandaAbiertaId(id); }} />
      )}
      {vista === "ingredientes" && <VistaIngredientes puedeRecetas={puedeRecetas} onCambioStock={cargarAlertasStock} />}

      {modalNuevaMesa && (
        <ModalMesa salones={salones} onClose={() => setModalNuevaMesa(false)} onGuardado={() => { setModalNuevaMesa(false); cargarMesas(); }} />
      )}
      {mesaEditar && (
        <ModalMesa mesa={mesaEditar} salones={salones} onClose={() => setMesaEditar(null)} onGuardado={() => { setMesaEditar(null); cargarMesas(); }} />
      )}
      {mesaParaAbrir && (
        <ModalAbrirComanda
          mesa={mesaParaAbrir}
          onClose={() => setMesaParaAbrir(null)}
          onAbierta={(id, offlineInfo) => {
            setMesaParaAbrir(null);
            if (offlineInfo) {
              patchMesaAbierta(mesaParaAbrir.id, offlineInfo.resumenMesa);
              setComandaInicial(offlineInfo.comandaInicial);
            } else {
              setComandaInicial(null);
              cargarMesas();
            }
            setComandaAbiertaId(id);
          }}
        />
      )}
      {modalSalones && (
        <ModalSalones salones={salones} onClose={() => setModalSalones(false)} onCambio={cargarSalones} />
      )}
      {comandaAbiertaId !== null && (
        <ModalComanda
          comandaId={comandaAbiertaId}
          comandaInicial={comandaInicial ?? undefined}
          onClose={() => { setComandaAbiertaId(null); setComandaInicial(null); cargarMesas(); cargarAlertasStock(); }}
          onDescartarTemporal={(mesaId) => { revertirMesaLibre(mesaId); setComandaAbiertaId(null); setComandaInicial(null); }}
          puedeGestionar={puedeGestionar}
          puedeCobrar={puedeCobrar}
          puedeAnular={puedeAnular}
        />
      )}
    </Shell>
  );
}

// --------------------------------------------------------- indicador offline
function IndicadorOffline({ enLinea, pendientes }: { enLinea: boolean; pendientes: AccionCola[] }) {
  const [abierto, setAbierto] = useState(false);
  if (enLinea && pendientes.length === 0) return null;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className={`flex items-center gap-1.5 text-xs rounded-lg border px-3 py-1.5 font-semibold ${enLinea ? "border-amber-300 bg-amber-50 text-amber-700" : "border-red-300 bg-red-50 text-red-700"}`}
      >
        <Icon name="wifiOff" className="h-3.5 w-3.5" />
        {enLinea ? `Sincronizando ${pendientes.length}...` : `Sin conexión — ${pendientes.length} pendiente${pendientes.length === 1 ? "" : "s"}`}
      </button>
      {abierto && (
        <div className="absolute right-0 mt-1 w-72 card p-3 z-20 max-h-72 overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-700">Acciones por sincronizar</span>
            <button type="button" className="text-[11px] text-accent-dark font-semibold" onClick={() => sincronizar()}>Reintentar ahora</button>
          </div>
          {pendientes.length === 0 ? (
            <p className="text-xs text-textmuted">No hay acciones pendientes.</p>
          ) : (
            <ul className="space-y-1.5">
              {pendientes.map((a) => (
                <li key={a.id} className="text-[11px] text-slate-600 border-b border-borderc pb-1 last:border-0">
                  {a.descripcion}
                  {a.ultimoError && <div className="text-red-600">Reintentando...</div>}
                </li>
              ))}
            </ul>
          )}
          <p className="text-[10px] text-textmuted mt-2">
            Se envían solas al recuperar internet. No cierre el navegador para no perderlas.
          </p>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------- vista mesas
function VistaMesas({ mesas, salones, onClicMesa, onEditar, puedeGestionar, modoEdicion, onMoverMesa, setError, modoUnificar, onSalirUnificar, onCambioMesas }: {
  mesas: Mesa[]; salones: Salon[]; onClicMesa: (m: Mesa) => void; onEditar: (m: Mesa) => void; puedeGestionar: boolean;
  modoEdicion: boolean; onMoverMesa: () => void; setError: (m: string | null) => void;
  modoUnificar: boolean; onSalirUnificar: () => void; onCambioMesas: (mesas: Mesa[]) => void;
}) {
  const [salonActivo, setSalonActivo] = useState<number | "sin_salon" | "todos">("todos");
  const [seleccionUnificar, setSeleccionUnificar] = useState<number[]>([]);
  const [enviandoUnificar, setEnviandoUnificar] = useState(false);

  useEffect(() => {
    if (!modoUnificar) setSeleccionUnificar([]);
  }, [modoUnificar]);

  // Solo mesas libres y sin ninguna vinculación previa pueden entrar en un
  // grupo nuevo (ver restaurante_utils.unificar_mesas — exige exactamente
  // eso del lado del backend; el filtro acá evita un 422 evitable).
  const esUnificable = (m: Mesa) => m.estado === "libre" && !m.mesaPrincipalId && !(m.mesasVinculadas && m.mesasVinculadas.length > 0);
  const toggleSeleccionUnificar = (m: Mesa) => {
    if (!esUnificable(m)) return;
    setSeleccionUnificar((prev) => (prev.includes(m.id) ? prev.filter((id) => id !== m.id) : [...prev, m.id]));
  };
  const confirmarUnificar = async () => {
    if (seleccionUnificar.length < 2) return;
    setEnviandoUnificar(true);
    setError(null);
    try {
      const [mesaPrincipalId, ...resto] = seleccionUnificar;
      const actualizadas: Mesa[] = await api.post("/restaurante/mesas/unificar", { mesaPrincipalId, mesaIds: resto });
      onCambioMesas(actualizadas);
      onSalirUnificar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudieron unificar las mesas.");
    } finally {
      setEnviandoUnificar(false);
    }
  };
  const separarMesas = async (mesaId: number) => {
    setError(null);
    try {
      const actualizadas: Mesa[] = await api.post(`/restaurante/mesas/${mesaId}/separar`, {});
      onCambioMesas(actualizadas);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo separar el grupo de mesas.");
    }
  };

  if (mesas.length === 0) {
    return (
      <div className="card p-10 text-center text-sm text-textmuted">
        {salones.length === 0
          ? <>Todavía no hay salones configurados.{puedeGestionar ? " Cree uno primero con \"Salones\" — se necesita al menos un salón antes de poder agregar mesas." : ""}</>
          : <>Todavía no hay mesas configuradas.{puedeGestionar ? " Cree la primera con \"Nueva mesa\"." : ""}</>}
      </div>
    );
  }

  const gruposOrdenados = [...salones].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
  const mesasDe = (salonId: number | null) => mesas.filter((m) => m.salonId === salonId);
  const sinSalon = mesasDe(null);
  // Las satélites solo traen el id de su principal (mesaPrincipalId) — para
  // mostrar el número de la principal en la tarjeta hace falta resolverlo acá.
  const numeroPorId: Record<number, string> = {};
  mesas.forEach((mm) => { numeroPorId[mm.id] = mm.numero; });

  if (!modoEdicion) {
    // Vista normal: tarjetas en cuadrícula, agrupadas por salón si hay más de uno.
    const secciones: { titulo: string | null; lista: Mesa[] }[] =
      gruposOrdenados.length === 0
        ? [{ titulo: null, lista: mesas }]
        : [...gruposOrdenados.map((s) => ({ titulo: s.nombre, lista: mesasDe(s.id) })), ...(sinSalon.length ? [{ titulo: "Sin salón asignado", lista: sinSalon }] : [])];
    return (
      <div className="space-y-6">
        {modoUnificar && (
          <div className="card p-3 flex items-center justify-between flex-wrap gap-2 bg-accent-light border-accent/40">
            <p className="text-xs text-accent-dark">
              Elija la mesa principal y luego 1 o más mesas para vincular (solo mesas libres y sin unir). La comanda del grupo se abrirá siempre desde la principal.
              {seleccionUnificar.length > 0 && (
                <span className="font-semibold"> Seleccionadas: {seleccionUnificar.length} (principal: mesa marcada primero).</span>
              )}
            </p>
            <button
              type="button" className="btn-primary text-xs disabled:opacity-60"
              disabled={seleccionUnificar.length < 2 || enviandoUnificar}
              onClick={confirmarUnificar}
            >
              {enviandoUnificar ? "Uniendo..." : `Unificar ${seleccionUnificar.length >= 2 ? seleccionUnificar.length : ""} mesas`}
            </button>
          </div>
        )}
        {secciones.filter((s) => s.lista.length > 0).map((sec, i) => (
          <div key={i}>
            {sec.titulo && <h3 className="text-xs font-semibold uppercase tracking-wide text-textmuted mb-2">{sec.titulo}</h3>}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {sec.lista.map((m) => (
                <TarjetaMesa
                  key={m.id} mesa={m} onClic={onClicMesa} onEditar={onEditar} puedeGestionar={puedeGestionar}
                  modoUnificar={modoUnificar} seleccionable={esUnificable(m)}
                  seleccionada={seleccionUnificar.includes(m.id)} onToggleSeleccion={toggleSeleccionUnificar}
                  onSeparar={separarMesas} principalNumero={m.mesaPrincipalId ? numeroPorId[m.mesaPrincipalId] : undefined}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Modo edición del plano: canvas con posición libre, por salón seleccionado.
  const salonesConTodos = [{ id: "todos" as const, nombre: "Todas (cuadrícula)" }, ...gruposOrdenados, ...(sinSalon.length ? [{ id: "sin_salon" as const, nombre: "Sin salón" }] : [])];
  return (
    <div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {salonesConTodos.map((s) => (
          <button
            key={String(s.id)} type="button" onClick={() => setSalonActivo(s.id as any)}
            className={`px-3 py-1 rounded-md text-xs font-semibold border ${salonActivo === s.id ? "border-accent bg-accent-light text-accent-dark" : "border-borderc text-slate-600 bg-white"}`}
          >
            {s.nombre}
          </button>
        ))}
      </div>
      {salonActivo === "todos" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {mesas.map((m) => <TarjetaMesa key={m.id} mesa={m} onClic={onClicMesa} onEditar={onEditar} puedeGestionar={puedeGestionar} />)}
        </div>
      ) : (
        <CanvasPlano
          mesas={salonActivo === "sin_salon" ? sinSalon : mesasDe(salonActivo as number)}
          onGuardarPosicion={onMoverMesa}
          setError={setError}
        />
      )}
      <p className="text-[11px] text-textmuted mt-3">Arrastre cada mesa para ubicarla en el plano. La posición se guarda automáticamente al soltar.</p>
    </div>
  );
}

function TarjetaMesa({
  mesa: m, onClic, onEditar, puedeGestionar,
  modoUnificar = false, seleccionable = false, seleccionada = false, onToggleSeleccion, onSeparar, principalNumero,
}: {
  mesa: Mesa; onClic: (m: Mesa) => void; onEditar: (m: Mesa) => void; puedeGestionar: boolean;
  modoUnificar?: boolean; seleccionable?: boolean; seleccionada?: boolean; onToggleSeleccion?: (m: Mesa) => void;
  onSeparar?: (mesaId: number) => void; principalNumero?: string;
}) {
  useReloj(30000);
  const esGrupo = !!m.mesaPrincipalId || !!(m.mesasVinculadas && m.mesasVinculadas.length > 0);
  const manejarClic = () => {
    if (modoUnificar) {
      if (seleccionable) onToggleSeleccion?.(m);
      return;
    }
    onClic(m);
  };
  return (
    <div
      className={`card p-4 border-2 relative ${ESTADO_MESA_ESTILO[m.estado] || ""} ${modoUnificar && !seleccionable ? "opacity-50" : ""} ${seleccionada ? "ring-2 ring-accent border-accent" : ""}`}
    >
      {puedeGestionar && !modoUnificar && (
        <button
          type="button"
          className="absolute top-2 right-2 text-slate-400 hover:text-slate-700"
          onClick={(e) => { e.stopPropagation(); onEditar(m); }}
          title="Editar mesa"
        >
          <Icon name="settings" className="h-3.5 w-3.5" />
        </button>
      )}
      <button type="button" className="w-full text-left disabled:cursor-not-allowed" onClick={manejarClic} disabled={modoUnificar && !seleccionable}>
        <div className="flex items-center gap-2 mb-1.5">
          <Icon name="utensils" className="h-4 w-4 opacity-70" />
          <span className="font-display font-bold text-sm">{m.numero}</span>
          {seleccionada && <Icon name="check" className="h-3.5 w-3.5 text-accent-dark" />}
        </div>
        {m.zona && <div className="text-[11px] opacity-70 mb-1">{m.zona}{m.capacidad ? ` · ${m.capacidad} puestos` : ""}</div>}
        <div className="badge bg-white/70 mt-1">{ESTADO_MESA_LABEL[m.estado] || m.estado}</div>
        {esGrupo && (
          <div className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-accent-dark">
            <Icon name="link" className="h-3 w-3" />
            {m.mesaPrincipalId
              ? `Vinculada a mesa ${principalNumero ?? m.mesaPrincipalId}`
              : `Unificada con ${m.mesasVinculadas!.map((v) => v.numero).join(", ")}`}
          </div>
        )}
        {m.comandaAbierta && (
          <div className="mt-2 text-xs">
            <div className="font-semibold flex items-center gap-1">
              {m.comandaAbierta.numero}
              {esIdTemporal(m.comandaAbierta.id) && <Icon name="wifiOff" className="h-3 w-3 text-amber-600" />}
            </div>
            <div className="opacity-70">{m.comandaAbierta.numComensales ? `${m.comandaAbierta.numComensales} comensales · ` : ""}{money(m.comandaAbierta.total)}</div>
            {m.comandaAbierta.meseroNombre && <div className="opacity-70">Mesero: {m.comandaAbierta.meseroNombre}</div>}
            <div className="flex items-center gap-1 opacity-70 mt-0.5">
              <Icon name="clock" className="h-3 w-3" /> Abierta hace {transcurrido(m.comandaAbierta.fechaApertura)}
            </div>
          </div>
        )}
        {m.estado === "por_limpiar" && !modoUnificar && <div className="mt-2 text-xs font-semibold">Clic para liberar</div>}
      </button>
      {esGrupo && puedeGestionar && !modoUnificar && (
        <button
          type="button"
          className="mt-2 text-[11px] font-semibold text-slate-500 hover:text-red-600 flex items-center gap-1"
          onClick={(e) => { e.stopPropagation(); onSeparar?.(m.id); }}
        >
          <Icon name="x" className="h-3 w-3" /> Separar mesas
        </button>
      )}
    </div>
  );
}

// -------------------------------------------------------- editor visual (canvas)
function CanvasPlano({ mesas, onGuardarPosicion, setError }: { mesas: Mesa[]; onGuardarPosicion: () => void; setError: (m: string | null) => void }) {
  const contenedorRef = useRef<HTMLDivElement>(null);
  const [posiciones, setPosiciones] = useState<Record<number, { x: number; y: number }>>({});
  const arrastrando = useRef<number | null>(null);

  useEffect(() => {
    const iniciales: Record<number, { x: number; y: number }> = {};
    mesas.forEach((m, i) => {
      iniciales[m.id] = {
        x: m.posX ?? (10 + (i % 5) * 18),
        y: m.posY ?? (10 + Math.floor(i / 5) * 28),
      };
    });
    setPosiciones(iniciales);
  }, [mesas]);

  const moverA = (mesaId: number, clientX: number, clientY: number) => {
    const rect = contenedorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.min(94, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    const y = Math.min(90, Math.max(0, ((clientY - rect.top) / rect.height) * 100));
    setPosiciones((prev) => ({ ...prev, [mesaId]: { x, y } }));
  };

  const onPointerDown = (mesaId: number) => (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    arrastrando.current = mesaId;
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (arrastrando.current == null) return;
    moverA(arrastrando.current, e.clientX, e.clientY);
  };
  const onPointerUp = async (mesaId: number) => async () => {
    if (arrastrando.current == null) return;
    arrastrando.current = null;
    const pos = posiciones[mesaId];
    if (!pos) return;
    try {
      await api.patch(`/restaurante/mesas/${mesaId}/posicion`, { posX: pos.x, posY: pos.y });
      onGuardarPosicion();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo guardar la posición de la mesa.");
    }
  };

  return (
    <div
      ref={contenedorRef}
      onPointerMove={onPointerMove}
      className="relative w-full h-[480px] rounded-xl border-2 border-dashed border-borderc bg-surface overflow-hidden select-none"
    >
      {mesas.length === 0 && <div className="absolute inset-0 flex items-center justify-center text-sm text-textmuted">No hay mesas en este salón todavía.</div>}
      {mesas.map((m) => {
        const pos = posiciones[m.id] || { x: 10, y: 10 };
        return (
          <div
            key={m.id}
            onPointerDown={onPointerDown(m.id)}
            onPointerUp={onPointerUp(m.id)}
            style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
            className={`absolute w-24 rounded-lg border-2 px-2 py-1.5 text-center shadow-sm cursor-grab active:cursor-grabbing ${ESTADO_MESA_ESTILO[m.estado] || "bg-white"}`}
          >
            <div className="text-xs font-bold font-display">{m.numero}</div>
            <div className="text-[9px]">{ESTADO_MESA_LABEL[m.estado]}</div>
          </div>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------------- vista cocina
function VistaCocina({ puedeCocina }: { puedeCocina: boolean }) {
  const [items, setItems] = useState<ItemCocina[] | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  useReloj(10000);

  const cargar = async () => {
    try {
      setItems(await api.get("/restaurante/cocina"));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar el tablero de cocina.");
    }
  };
  useEffect(() => {
    cargar();
    const intervalo = setInterval(cargar, 15000);
    return () => clearInterval(intervalo);
  }, []);

  const avanzar = async (item: ItemCocina) => {
    const siguiente = SIGUIENTE_ESTADO_COCINA[item.estadoCocina];
    if (!siguiente) return;
    setError(null);
    try {
      const r = await cambiarEstadoItemOffline(item.comandaId, item.id, siguiente, `${item.cantidad}× ${item.producto} → ${ESTADO_COCINA_LABEL[siguiente]} (mesa ${item.mesaNumero})`);
      if (r.offline) {
        // Actualización optimista: se refleja en el tablero de inmediato aunque
        // el cambio real quede en la cola hasta que vuelva la conexión.
        setItems((prev) => prev?.map((it) => (it.id === item.id ? { ...it, estadoCocina: siguiente } : it)));
      } else {
        cargar();
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo actualizar el estado.");
    }
  };

  if (items === undefined) return <div className="card p-10 text-center text-sm text-textmuted">Cargando...</div>;

  return (
    <div>
      {error && <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
      {items.length === 0 ? (
        <div className="card p-10 text-center text-sm text-textmuted">No hay ítems pendientes por preparar o entregar.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((it) => {
            const min = minutosDesde(it.creadoEn);
            return (
              <div key={it.id} className="card p-4">
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div>
                    <div className="text-sm font-semibold text-slate-800">Mesa {it.mesaNumero}</div>
                    <div className="text-[11px] text-textmuted">{it.comandaNumero}</div>
                  </div>
                  <span className={`badge ${ESTADO_COCINA_ESTILO[it.estadoCocina]}`}>{ESTADO_COCINA_LABEL[it.estadoCocina]}</span>
                </div>
                <div className="text-sm text-slate-700 font-medium">{it.cantidad} × {it.producto}</div>
                {it.observaciones && <div className="text-xs text-amber-700 mt-1">Nota: {it.observaciones}</div>}
                {!!it.chips?.length && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {it.chips.map((c) => (
                      <span key={c} className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-accent-light text-accent-dark">{CHIP_ETIQUETA[c] || c}</span>
                    ))}
                  </div>
                )}
                <div className={`text-[11px] mt-1 flex items-center gap-1 ${urgenciaCocina(min)}`}>
                  <Icon name="clock" className="h-3 w-3" /> Hace {transcurrido(it.creadoEn)}
                </div>
                {puedeCocina && SIGUIENTE_ESTADO_COCINA[it.estadoCocina] && (
                  <button type="button" className="btn-secondary w-full mt-3 text-xs" onClick={() => avanzar(it)}>
                    Marcar {ESTADO_COCINA_LABEL[SIGUIENTE_ESTADO_COCINA[it.estadoCocina]!]}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------- vista delivery
function VistaDelivery({ puedeGestionar, onAbrirComanda }: { puedeGestionar: boolean; onAbrirComanda: (id: number) => void }) {
  const [pedidos, setPedidos] = useState<PedidoDelivery[] | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [modalNuevo, setModalNuevo] = useState(false);
  useReloj(30000);

  const cargar = async () => {
    try {
      setPedidos(await api.get("/restaurante/delivery"));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar el listado de delivery.");
    }
  };
  useEffect(() => {
    cargar();
    const intervalo = setInterval(cargar, 20000);
    return () => clearInterval(intervalo);
  }, []);

  const avanzarEstado = async (p: PedidoDelivery) => {
    const idx = ESTADOS_DELIVERY.indexOf(p.estadoDelivery as any);
    const siguiente = ESTADOS_DELIVERY[idx + 1];
    if (!siguiente) return;
    setError(null);
    try {
      await api.patch(`/restaurante/delivery/${p.id}/estado`, { estado: siguiente });
      cargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo actualizar el estado del pedido.");
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        {puedeGestionar && (
          <button type="button" className="btn-primary flex items-center gap-1.5" onClick={() => setModalNuevo(true)}>
            <Icon name="plus" className="h-4 w-4" /> Nuevo pedido delivery
          </button>
        )}
      </div>
      {error && <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
      {pedidos === undefined ? (
        <div className="card p-10 text-center text-sm text-textmuted">Cargando...</div>
      ) : pedidos.length === 0 ? (
        <div className="card p-10 text-center text-sm text-textmuted">No hay pedidos a domicilio activos.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {pedidos.map((p) => (
            <div key={p.id} className="card p-4">
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-1.5">
                  <Icon name="truck" className="h-4 w-4 opacity-70" />
                  <span className="font-display font-bold text-sm">{p.numero}</span>
                </div>
                <span className="badge bg-accent-light text-accent-dark">{ESTADO_DELIVERY_LABEL[p.estadoDelivery]}</span>
              </div>
              {p.clienteNombre && <div className="text-xs text-slate-700">{p.clienteNombre}</div>}
              {p.telefonoEntrega && <div className="text-xs text-textmuted">{p.telefonoEntrega}</div>}
              {p.direccionEntrega && <div className="text-xs text-textmuted mb-1">{p.direccionEntrega}</div>}
              <div className="text-xs text-slate-600 mt-1">{p.items.length} ítem(s) · {money(p.total)}</div>
              <div className="flex items-center gap-1 text-[11px] text-textmuted mt-1">
                <Icon name="clock" className="h-3 w-3" /> Hace {transcurrido(p.fechaApertura)}
              </div>
              <div className="flex gap-2 mt-3">
                {puedeGestionar && ESTADOS_DELIVERY.indexOf(p.estadoDelivery as any) < ESTADOS_DELIVERY.length - 1 && (
                  <button type="button" className="btn-secondary flex-1 text-xs" onClick={() => avanzarEstado(p)}>
                    Marcar {ESTADO_DELIVERY_LABEL[ESTADOS_DELIVERY[ESTADOS_DELIVERY.indexOf(p.estadoDelivery as any) + 1]]}
                  </button>
                )}
                <button type="button" className="btn-ghost flex-1 text-xs" onClick={() => onAbrirComanda(p.id)}>Ver / cobrar</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {modalNuevo && (
        <ModalNuevoDelivery onClose={() => setModalNuevo(false)} onCreado={() => { setModalNuevo(false); cargar(); }} />
      )}
    </div>
  );
}

function ModalNuevoDelivery({ onClose, onCreado }: { onClose: () => void; onCreado: () => void }) {
  const [clienteNombre, setClienteNombre] = useState("");
  const [telefono, setTelefono] = useState("");
  const [direccion, setDireccion] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [productos, setProductos] = useState<ProductoDisponible[]>([]);
  const [items, setItems] = useState<{ inventarioPtId: number; producto: string; cantidad: number; precio: number }[]>([]);
  const [productoId, setProductoId] = useState<number | "">("");
  const [cantidad, setCantidad] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => { api.get("/pos/productos-disponibles").then(setProductos).catch(() => setProductos([])); }, []);

  const agregarItem = () => {
    if (!productoId) return;
    const p = productos.find((pr) => pr.id === productoId);
    if (!p) return;
    setItems((prev) => [...prev, { inventarioPtId: p.id, producto: p.producto, cantidad, precio: p.precioSugerido || 0 }]);
    setProductoId("");
    setCantidad(1);
  };
  const quitarItem = (i: number) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!direccion.trim()) { setError("La dirección de entrega es obligatoria."); return; }
    if (items.length === 0) { setError("Agregue al menos un producto al pedido."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.post("/restaurante/delivery", {
        clienteNombre, telefono, direccion, observaciones,
        items: items.map((it) => ({ inventarioPtId: it.inventarioPtId, cantidad: it.cantidad, precio: it.precio })),
      });
      onCreado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo crear el pedido.");
    } finally {
      setEnviando(false);
    }
  };

  const total = items.reduce((s, it) => s + it.cantidad * it.precio, 0);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-6">
      <form onSubmit={crear} className="card w-full max-w-md max-h-full overflow-y-auto p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800">Nuevo pedido delivery</h3>
          <button type="button" onClick={onClose} className="text-textmuted hover:text-slate-700"><Icon name="x" className="h-4 w-4" /></button>
        </div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Cliente</label>
          <input className="input-field" value={clienteNombre} onChange={(e) => setClienteNombre(e.target.value)} autoFocus /></div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Teléfono</label>
          <input className="input-field" value={telefono} onChange={(e) => setTelefono(e.target.value)} /></div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Dirección de entrega *</label>
          <input className="input-field" value={direccion} onChange={(e) => setDireccion(e.target.value)} required /></div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Observaciones</label>
          <input className="input-field" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} /></div>

        <div className="rounded-lg border border-dashed border-borderc p-3 space-y-2">
          <select className="input-field text-xs" value={productoId} onChange={(e) => setProductoId(e.target.value ? Number(e.target.value) : "")}>
            <option value="">Agregar plato/producto...</option>
            {productos.map((p) => <option key={p.id} value={p.id} disabled={p.cantidadDisponible <= 0}>{p.producto} ({p.cantidadDisponible} disp.)</option>)}
          </select>
          {productoId !== "" && (
            <div className="flex gap-2">
              <input className="input-field text-xs flex-1" type="number" min={1} step="1" value={cantidad} onChange={(e) => setCantidad(Number(e.target.value))} />
              <button type="button" className="btn-secondary text-xs" onClick={agregarItem}>Agregar</button>
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="space-y-1">
            {items.map((it, i) => (
              <div key={i} className="flex items-center justify-between text-xs rounded-md border border-borderc px-2.5 py-1.5">
                <span>{it.cantidad} × {it.producto}</span>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{money(it.cantidad * it.precio)}</span>
                  <button type="button" className="text-red-500" onClick={() => quitarItem(i)}><Icon name="trash" className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
            <div className="flex justify-between text-sm font-semibold pt-1"><span>Total</span><span>{money(total)}</span></div>
          </div>
        )}

        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Creando..." : "Crear pedido"}</button>
        </div>
      </form>
    </div>
  );
}

// ----------------------------------------------------------------- modal: crear/editar mesa
function ModalMesa({ mesa, salones, onClose, onGuardado }: { mesa?: Mesa; salones: Salon[]; onClose: () => void; onGuardado: () => void }) {
  const [numero, setNumero] = useState(mesa?.numero || "");
  const [zona, setZona] = useState(mesa?.zona || "");
  const [capacidad, setCapacidad] = useState<number | "">(mesa?.capacidad ?? "");
  const [salonId, setSalonId] = useState<number | "">(mesa?.salonId ?? "");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const body = { numero, zona, capacidad: capacidad === "" ? null : capacidad, salonId: salonId === "" ? null : salonId };
      if (mesa) await api.put(`/restaurante/mesas/${mesa.id}`, body);
      else await api.post("/restaurante/mesas", body);
      onGuardado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo guardar la mesa.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={guardar} className="card w-full max-w-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800">{mesa ? "Editar mesa" : "Nueva mesa"}</h3>
          <button type="button" onClick={onClose} className="text-textmuted hover:text-slate-700"><Icon name="x" className="h-4 w-4" /></button>
        </div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Número / nombre *</label>
          <input className="input-field" value={numero} onChange={(e) => setNumero(e.target.value)} required autoFocus /></div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Zona (texto libre)</label>
          <input className="input-field" value={zona} onChange={(e) => setZona(e.target.value)} placeholder="Salón principal, terraza..." /></div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Salón formal</label>
          <select className="input-field" value={salonId} onChange={(e) => setSalonId(e.target.value ? Number(e.target.value) : "")}>
            <option value="">Sin salón asignado</option>
            {salones.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
          </select></div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Capacidad (personas)</label>
          <input className="input-field" type="number" min={1} value={capacidad} onChange={(e) => setCapacidad(e.target.value ? Number(e.target.value) : "")} /></div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Guardando..." : "Guardar"}</button>
        </div>
      </form>
    </div>
  );
}

// ----------------------------------------------------------------- modal: salones
function ModalSalones({ salones, onClose, onCambio }: { salones: Salon[]; onClose: () => void; onCambio: () => void }) {
  const [nombre, setNombre] = useState("");
  const [editando, setEditando] = useState<Salon | null>(null);
  const [error, setError] = useState<string | null>(null);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombre.trim()) return;
    setError(null);
    try {
      await api.post("/restaurante/salones", { nombre, orden: salones.length });
      setNombre("");
      onCambio();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo crear el salón.");
    }
  };

  const guardarEdicion = async () => {
    if (!editando) return;
    setError(null);
    try {
      await api.put(`/restaurante/salones/${editando.id}`, { nombre: editando.nombre, orden: editando.orden });
      setEditando(null);
      onCambio();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo editar el salón.");
    }
  };

  const eliminar = async (id: number) => {
    setError(null);
    try {
      await api.del(`/restaurante/salones/${id}`);
      onCambio();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo eliminar el salón.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <div className="card w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800">Salones / zonas</h3>
          <button type="button" onClick={onClose} className="text-textmuted hover:text-slate-700"><Icon name="x" className="h-4 w-4" /></button>
        </div>
        <form onSubmit={crear} className="flex gap-2">
          <input className="input-field flex-1" placeholder="Nombre del salón (Terraza, Salón principal...)" value={nombre} onChange={(e) => setNombre(e.target.value)} />
          <button type="submit" className="btn-primary text-xs">Agregar</button>
        </form>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {salones.length === 0 && <p className="text-xs text-textmuted italic">Sin salones creados. Las mesas quedan agrupadas en "Sin salón".</p>}
          {salones.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-2 rounded-lg border border-borderc px-3 py-2">
              {editando?.id === s.id ? (
                <input className="input-field text-xs flex-1" value={editando.nombre} onChange={(e) => setEditando({ ...editando, nombre: e.target.value })} autoFocus />
              ) : (
                <span className="text-sm text-slate-700">{s.nombre}</span>
              )}
              <div className="flex items-center gap-2 shrink-0">
                {editando?.id === s.id ? (
                  <button type="button" className="text-emerald-600" onClick={guardarEdicion}><Icon name="check" className="h-4 w-4" /></button>
                ) : (
                  <button type="button" className="text-slate-400 hover:text-slate-700" onClick={() => setEditando(s)}><Icon name="settings" className="h-3.5 w-3.5" /></button>
                )}
                <button type="button" className="text-red-500 hover:text-red-700" onClick={() => eliminar(s.id)}><Icon name="trash" className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- modal: abrir comanda (sentar mesa)
// `onAbierta` recibe siempre un ID de comanda (real o temporal). Cuando la
// apertura quedó encolada sin conexión, también llega `offlineInfo` con la
// comanda provisional completa (para sembrar ModalComanda sin red) y el
// resumen que se le pega de forma optimista a la mesa en pantalla.
type ComandaAbiertaOffline = { comandaInicial: Comanda; resumenMesa: NonNullable<Mesa["comandaAbierta"]> };

function ModalAbrirComanda({ mesa, onClose, onAbierta }: {
  mesa: Mesa; onClose: () => void; onAbierta: (comandaId: number, offlineInfo?: ComandaAbiertaOffline) => void;
}) {
  const [numComensales, setNumComensales] = useState<number | "">("");
  const [observaciones, setObservaciones] = useState("");
  const [meseros, setMeseros] = useState<Mesero[]>([]);
  const [meseroId, setMeseroId] = useState<number | "">("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    api.get("/restaurante/meseros")
      .then((m) => { setMeseros(m); guardarCacheMeseros(m); })
      .catch(() => leerCacheMeseros().then(setMeseros));
  }, []);

  const abrir = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    const body = {
      numComensales: numComensales === "" ? null : numComensales, observaciones,
      meseroId: meseroId === "" ? null : (meseroId as number),
    };
    try {
      const resultado = await abrirComandaOffline(mesa.id, body, `Sentar mesa ${mesa.numero}`);
      if (resultado.offline) {
        const ahora = new Date().toISOString();
        const meseroNombre = body.meseroId ? (meseros.find((m) => m.id === body.meseroId)?.nombre ?? null) : null;
        const comandaInicial: Comanda = {
          id: resultado.tempId, numero: "(sin sincronizar)", mesaId: mesa.id, mesaNumero: mesa.numero, turnoCajaId: null,
          estado: "abierta", numComensales: body.numComensales, observaciones: observaciones || null, ventaPosId: null,
          usuarioAperturaNombre: null, meseroId: body.meseroId, meseroNombre,
          tipo: "mesa", estadoDelivery: null, clienteId: null, clienteNombre: null, telefonoEntrega: null, direccionEntrega: null,
          fechaApertura: ahora, fechaCierre: null, items: [], total: 0,
        };
        onAbierta(resultado.tempId, {
          comandaInicial,
          resumenMesa: {
            id: resultado.tempId, numero: "(sin sincronizar)", estado: "abierta",
            numComensales: body.numComensales, fechaApertura: ahora, total: 0, meseroNombre,
          },
        });
      } else {
        onAbierta(resultado.data.id);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo abrir la comanda.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={abrir} className="card w-full max-w-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800">Sentar mesa {mesa.numero}</h3>
          <button type="button" onClick={onClose} className="text-textmuted hover:text-slate-700"><Icon name="x" className="h-4 w-4" /></button>
        </div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Comensales</label>
          <input className="input-field" type="number" min={1} value={numComensales} onChange={(e) => setNumComensales(e.target.value ? Number(e.target.value) : "")} autoFocus /></div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Mesero que atiende</label>
          <select className="input-field" value={meseroId} onChange={(e) => setMeseroId(e.target.value ? Number(e.target.value) : "")}>
            <option value="">Quien la abre (usted)</option>
            {meseros.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
          </select></div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Observaciones</label>
          <input className="input-field" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} placeholder="Alergias, ocasión especial..." /></div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Abriendo..." : "Abrir comanda"}</button>
        </div>
      </form>
    </div>
  );
}

// ----------------------------------------------------------------- modal: comanda (gestión completa)
function ModalComanda({ comandaId, comandaInicial, onClose, onDescartarTemporal, puedeGestionar, puedeCobrar, puedeAnular }: {
  comandaId: number; comandaInicial?: Comanda; onClose: () => void; onDescartarTemporal?: (mesaId: number) => void;
  puedeGestionar: boolean; puedeCobrar: boolean; puedeAnular: boolean;
}) {
  const [comanda, setComanda] = useState<Comanda | undefined>(comandaInicial);
  const [productos, setProductos] = useState<ProductoDisponible[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [avisoOffline, setAvisoOffline] = useState<string | null>(null);
  const [seccionCobro, setSeccionCobro] = useState<false | "todo" | number>(false);
  const [pendientesComanda, setPendientesComanda] = useState<AccionCola[]>([]);
  useReloj(30000);

  const cargar = async () => {
    if (esIdTemporal(comandaId)) {
      // Esta comanda se abrió sin conexión y todavía no existe en el
      // servidor — no tiene sentido pedirla por red (respondería 404 real,
      // no un fallo de red). Se sigue mostrando la copia local con la que
      // se sembró el modal (`comandaInicial`), que ya va reflejando cada
      // acción que se encola sobre ella.
      return;
    }
    try {
      const data = await api.get(`/restaurante/comandas/${comandaId}`);
      setComanda(data);
      guardarCacheComanda(data);
      setError(null);
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
      } else {
        // Fallo de red: se muestra la última copia buena que se tenga en
        // caché (si existe) para que el mesero no se quede sin ver la
        // comanda solo porque se recargó la pantalla estando sin conexión.
        const cache = await leerCacheComanda(comandaId);
        if (cache) {
          setComanda(cache);
          setError("Sin conexión — mostrando la última versión conocida de esta comanda.");
        } else {
          setError("No se pudo cargar la comanda (sin conexión y sin copia local).");
        }
      }
    }
  };
  useEffect(() => {
    cargar();
    api.get("/pos/productos-disponibles")
      .then((p) => { setProductos(p); guardarCacheProductos(p); })
      .catch(() => { leerCacheProductos().then(setProductos); });
    listarColaDeComanda(comandaId).then(setPendientesComanda);
    const unsub = suscribirCola(async () => setPendientesComanda(await listarColaDeComanda(comandaId)));
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comandaId]);

  const comandaActiva = comanda && comanda.estado !== "cerrada" && comanda.estado !== "anulada";
  const esDelivery = comanda?.tipo === "delivery";
  const esMostrador = comanda?.tipo === "mostrador";
  const sinSincronizar = esIdTemporal(comandaId);

  const enviarACocina = async () => {
    setError(null);
    setAvisoOffline(null);
    try {
      const r = await enviarACocinaOffline(comandaId, `Enviar a cocina — ${comanda?.numero}`);
      if (r.offline) {
        setAvisoOffline("Sin conexión: se enviará a cocina automáticamente al reconectar.");
        setComanda((prev) => (prev ? { ...prev, estado: "enviada_cocina", items: prev.items?.map((it) => ({ ...it, enviadoCocina: true })) } : prev));
      } else {
        cargar();
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo enviar a cocina.");
    }
  };

  const itemsNuevos: ItemComanda[] = comanda?.estado !== "abierta" ? (comanda?.items?.filter((it: ItemComanda) => !it.enviadoCocina) || []) : [];

  // Cuentas con al menos un ítem sin cobrar todavía — "Junto" (sin dividir)
  // siempre resuelve en un solo elemento (cuenta 1), así que el flujo normal
  // sigue viendo un único botón "Cerrar y cobrar" como antes; solo cuando
  // hay más de una cuenta pendiente (alguien usó "Pasar" en algún ítem) se
  // ofrece un botón de cobro POR cuenta en vez de uno solo para toda la
  // comanda.
  const itemsPendientesPago: ItemComanda[] = comanda?.items?.filter((it: ItemComanda) => !it.pagado) || [];
  const cuentasPendientes: number[] = Array.from(new Set(itemsPendientesPago.map((it: ItemComanda) => it.cuenta || 1))).sort((a: number, b: number) => a - b);
  const subtotalCuenta = (cta: number) => itemsPendientesPago.filter((it: ItemComanda) => (it.cuenta || 1) === cta).reduce((s: number, it: ItemComanda) => s + it.cantidad * it.precio, 0);

  const reenviarACocina = async () => {
    setError(null);
    setAvisoOffline(null);
    try {
      const r = await reenviarACocinaOffline(comandaId, `Reenviar a cocina (${itemsNuevos.length} nuevo(s)) — ${comanda?.numero}`);
      if (r.offline) {
        setAvisoOffline("Sin conexión: los platos nuevos se enviarán a cocina automáticamente al reconectar.");
        setComanda((prev) => (prev ? { ...prev, items: prev.items?.map((it) => ({ ...it, enviadoCocina: true })) } : prev));
      } else {
        cargar();
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo reenviar a cocina.");
    }
  };

  const anular = async () => {
    if (sinSincronizar) {
      // Esta comanda nunca llegó a existir en el servidor: "anularla" es
      // simplemente descartar la apertura y todo lo que se le haya agregado
      // mientras estaba en la cola, y devolver la mesa a libre en pantalla.
      setError(null);
      await descartarComandaTemporal(comandaId);
      if (comanda) onDescartarTemporal?.(comanda.mesaId);
      onClose();
      return;
    }
    // Anular una comanda que sí existe en el servidor requiere conexión
    // real: revierte inventario/estado de mesa de forma que no es seguro
    // dejar en cola (podría chocar con acciones ya encoladas sobre la misma
    // comanda).
    setError(null);
    try {
      await api.post(`/restaurante/comandas/${comandaId}/anular`);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo anular la comanda.");
    }
  };

  const avanzarDelivery = async () => {
    if (!comanda?.estadoDelivery) return;
    const idx = ESTADOS_DELIVERY.indexOf(comanda.estadoDelivery as any);
    const siguiente = ESTADOS_DELIVERY[idx + 1];
    if (!siguiente) return;
    setError(null);
    try {
      await api.patch(`/restaurante/delivery/${comandaId}/estado`, { estado: siguiente });
      cargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo actualizar el estado del pedido.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-6">
      <div className="card w-full max-w-lg max-h-full overflow-y-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
              {comanda ? (esDelivery ? `${comanda.numero} — Domicilio` : esMostrador ? `${comanda.numero} — Venta rápida` : `${comanda.numero} — Mesa ${comanda.mesaNumero}`) : "Comanda"}
              {sinSincronizar && (
                <span className="badge bg-amber-50 text-amber-700 flex items-center gap-1">
                  <Icon name="wifiOff" className="h-3 w-3" /> Sin sincronizar
                </span>
              )}
            </h3>
            {comanda && (
              <p className="text-xs text-textmuted flex items-center gap-2 flex-wrap">
                {comanda.numComensales ? `${comanda.numComensales} comensales · ` : ""}Estado: {comanda.estado}
                {comanda.meseroNombre && ` · Mesero: ${comanda.meseroNombre}`}
                <span className="flex items-center gap-1"><Icon name="clock" className="h-3 w-3" /> hace {transcurrido(comanda.fechaApertura)}</span>
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} className="text-textmuted hover:text-slate-700"><Icon name="x" className="h-4 w-4" /></button>
        </div>

        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        {avisoOffline && <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700 flex items-center gap-1.5"><Icon name="wifiOff" className="h-3.5 w-3.5" />{avisoOffline}</div>}
        {pendientesComanda.length > 0 && (
          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
            {pendientesComanda.length} acción(es) de esta comanda pendiente(s) de sincronizar.
          </div>
        )}

        {!comanda && <div className="text-center text-sm text-textmuted py-6">Cargando...</div>}

        {comanda && (
          <>
            {esDelivery && (
              <div className="rounded-lg border border-borderc p-3 text-xs space-y-1">
                {comanda.clienteNombre && <div><span className="font-semibold">Cliente:</span> {comanda.clienteNombre}</div>}
                {comanda.telefonoEntrega && <div><span className="font-semibold">Teléfono:</span> {comanda.telefonoEntrega}</div>}
                {comanda.direccionEntrega && <div><span className="font-semibold">Dirección:</span> {comanda.direccionEntrega}</div>}
                {comanda.estadoDelivery && (
                  <div className="flex items-center justify-between pt-1">
                    <span className="badge bg-accent-light text-accent-dark">{ESTADO_DELIVERY_LABEL[comanda.estadoDelivery]}</span>
                    {puedeGestionar && ESTADOS_DELIVERY.indexOf(comanda.estadoDelivery as any) < ESTADOS_DELIVERY.length - 1 && comandaActiva && (
                      <button type="button" className="btn-secondary text-xs" onClick={avanzarDelivery}>
                        Marcar {ESTADO_DELIVERY_LABEL[ESTADOS_DELIVERY[ESTADOS_DELIVERY.indexOf(comanda.estadoDelivery as any) + 1]]}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {puedeGestionar && comandaActiva && !esDelivery && (
              <SelectorMesero comanda={comanda} onCambio={cargar} setError={setError} />
            )}

            {comanda.observaciones && <p className="text-xs text-slate-600 italic">Obs: {comanda.observaciones}</p>}

            <ListaItems comanda={comanda} puedeGestionar={puedeGestionar && !!comandaActiva} onCambio={cargar} setComanda={setComanda} setError={setError} setAvisoOffline={setAvisoOffline} />

            {comandaActiva && puedeGestionar && itemsNuevos.length > 0 && (
              <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex items-center justify-between gap-2 flex-wrap">
                <span className="flex items-center gap-1.5">
                  <span className="badge bg-amber-100 text-amber-800 font-bold">NUEVO</span>
                  {itemsNuevos.length} plato{itemsNuevos.length === 1 ? "" : "s"} sin enviar a cocina.
                </span>
                <button type="button" className="btn-secondary text-xs" onClick={reenviarACocina}>Reenviar a cocina</button>
              </div>
            )}

            {comandaActiva && puedeGestionar && (
              <FormAgregarItem comandaId={comandaId} productos={productos} onAgregado={cargar} setComanda={setComanda} setError={setError} setAvisoOffline={setAvisoOffline} />
            )}

            <div className="flex justify-between items-center pt-2 border-t border-borderc">
              <span className="text-sm font-semibold text-slate-800">Total</span>
              <span className="text-base font-bold text-slate-800">{money(comanda.total || 0)}</span>
            </div>

            {comandaActiva && (
              <div className="flex flex-wrap gap-2 pt-2">
                {puedeGestionar && comanda.estado === "abierta" && (comanda.items?.length || 0) > 0 && (
                  <button type="button" className="btn-secondary flex-1" onClick={enviarACocina}>Enviar a cocina</button>
                )}
                {puedeCobrar && cuentasPendientes.length <= 1 && (comanda.items?.length || 0) > 0 && (
                  <button type="button" className="btn-primary flex-1" onClick={() => setSeccionCobro((v) => (v ? false : "todo"))}>
                    {seccionCobro ? "Ocultar cobro" : "Cerrar y cobrar"}
                  </button>
                )}
                {puedeAnular && (
                  <button type="button" className="btn-ghost text-red-600 flex-1" onClick={anular}>Anular comanda</button>
                )}
              </div>
            )}

            {comandaActiva && puedeCobrar && cuentasPendientes.length > 1 && (
              <div className="space-y-1.5 pt-1">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Cuentas separadas — cobrar por separado</p>
                <div className="flex flex-wrap gap-2">
                  {cuentasPendientes.map((cta) => (
                    <button
                      key={cta} type="button"
                      className={`flex-1 rounded-lg border py-1.5 text-xs font-semibold transition-colors ${seccionCobro === cta ? "border-accent bg-accent text-white" : "border-borderc text-slate-600 hover:border-slate-300 bg-white"}`}
                      onClick={() => setSeccionCobro((v) => (v === cta ? false : cta))}
                    >
                      Cobrar cuenta {cta} ({money(subtotalCuenta(cta))})
                    </button>
                  ))}
                </div>
                <button type="button" className="text-[11px] text-accent-dark font-semibold underline" onClick={() => setSeccionCobro((v) => (v === "todo" ? false : "todo"))}>
                  {seccionCobro === "todo" ? "Ocultar" : "O cobrar todo lo pendiente de una vez"}
                </button>
              </div>
            )}

            {comandaActiva && seccionCobro && puedeCobrar && (
              <FormCobrarComanda
                comandaId={comandaId}
                cuenta={typeof seccionCobro === "number" ? seccionCobro : undefined}
                esUltimaCuentaPendiente={seccionCobro === "todo" || cuentasPendientes.length <= 1}
                onCobrado={(cerrada) => {
                  setSeccionCobro(false);
                  if (cerrada) onClose();
                  else cargar();
                }}
                setError={setError}
                setAvisoOffline={setAvisoOffline}
              />
            )}

            {comanda.estado === "cerrada" && (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                Esta comanda ya fue cobrada (venta {comanda.ventaPosId ? `#${comanda.ventaPosId}` : ""}). Consulte el comprobante en POS → Ventas.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SelectorMesero({ comanda, onCambio, setError }: { comanda: Comanda; onCambio: () => void; setError: (m: string | null) => void }) {
  const [meseros, setMeseros] = useState<Mesero[]>([]);
  const [editando, setEditando] = useState(false);
  const [meseroId, setMeseroId] = useState<number | "">(comanda.meseroId ?? "");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!editando) return;
    api.get("/restaurante/meseros")
      .then((m) => { setMeseros(m); guardarCacheMeseros(m); })
      .catch(() => leerCacheMeseros().then(setMeseros));
  }, [editando]);

  const guardar = async () => {
    if (!meseroId) return;
    setEnviando(true);
    setError(null);
    try {
      await api.patch(`/restaurante/comandas/${comanda.id}/mesero`, { meseroId });
      setEditando(false);
      onCambio();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo reasignar el mesero.");
    } finally {
      setEnviando(false);
    }
  };

  if (!editando) {
    return (
      <div className="flex items-center justify-between text-xs text-slate-600">
        <span>Mesero: <span className="font-semibold">{comanda.meseroNombre || "Sin asignar"}</span></span>
        <button type="button" className="text-accent-dark font-semibold" onClick={() => setEditando(true)}>Reasignar</button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <select className="input-field text-xs flex-1" value={meseroId} onChange={(e) => setMeseroId(e.target.value ? Number(e.target.value) : "")}>
        <option value="">Seleccione mesero...</option>
        {meseros.map((m) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
      </select>
      <button type="button" className="btn-secondary text-xs" disabled={enviando} onClick={guardar}>Guardar</button>
      <button type="button" className="text-textmuted" onClick={() => setEditando(false)}><Icon name="x" className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function ListaItems({ comanda, puedeGestionar, onCambio, setComanda, setError, setAvisoOffline }: {
  comanda: Comanda; puedeGestionar: boolean; onCambio: () => void; setComanda: (c: Comanda) => void;
  setError: (m: string | null) => void; setAvisoOffline: (m: string | null) => void;
}) {
  const quitar = async (itemId: number, descripcion: string) => {
    setError(null);
    setAvisoOffline(null);
    try {
      const r = await quitarItemOffline(comanda.id, itemId, descripcion);
      if (r.offline) {
        setAvisoOffline("Sin conexión: el ítem se quitará al sincronizar.");
        setComanda({ ...comanda, items: comanda.items?.filter((it) => it.id !== itemId) });
      } else {
        onCambio();
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo quitar el ítem.");
    }
  };

  if (!comanda.items || comanda.items.length === 0) {
    return <p className="text-xs text-textmuted italic">Todavía no hay ítems en esta comanda.</p>;
  }

  // Agrupar por cuenta (todos empiezan en la 1 = "Junto") — solo se muestran
  // los encabezados de cuenta cuando hay más de una, para no meterle ruido
  // visual al caso normal (sin dividir) que sigue siendo la mayoría.
  const cuentas = Array.from(new Set(comanda.items.map((it) => it.cuenta || 1))).sort((a, b) => a - b);
  const mostrarCuentas = cuentas.length > 1;

  return (
    <div className="space-y-3">
      {cuentas.map((cta) => {
        const itemsCta = comanda.items!.filter((it) => (it.cuenta || 1) === cta);
        const subtotal = itemsCta.reduce((s, it) => s + it.cantidad * it.precio, 0);
        return (
          <div key={cta} className="space-y-1.5">
            {mostrarCuentas && (
              <div className="flex items-center justify-between text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                <span>Cuenta {cta}</span>
                <span>{money(subtotal)}</span>
              </div>
            )}
            {itemsCta.map((it) => (
              <div key={it.id} className="flex items-center justify-between gap-2 rounded-lg border border-borderc px-3 py-2">
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-slate-800 truncate">{it.cantidad} × {it.producto}</div>
                  {it.observaciones && <div className="text-[10px] text-amber-700 truncate">{it.observaciones}</div>}
                  {!!it.chips?.length && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {it.chips.map((c) => (
                        <span key={c} className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-accent-light text-accent-dark">{CHIP_ETIQUETA[c] || c}</span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {comanda.estado !== "abierta" && !it.enviadoCocina && (
                      <span className="badge bg-amber-100 text-amber-800 font-bold">NUEVO</span>
                    )}
                    <span className={`badge ${ESTADO_COCINA_ESTILO[it.estadoCocina]}`}>{ESTADO_COCINA_LABEL[it.estadoCocina]}</span>
                    <span className="text-[10px] text-textmuted flex items-center gap-0.5"><Icon name="clock" className="h-2.5 w-2.5" />{transcurrido(it.creadoEn)}</span>
                    {it.pagado && <span className="badge bg-emerald-50 text-emerald-700">Pagado</span>}
                    {puedeGestionar && !it.pagado && (
                      <SelectorCuenta comanda={comanda} item={it} onCambio={onCambio} setError={setError} setAvisoOffline={setAvisoOffline} />
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs font-semibold text-slate-800">{money(it.cantidad * it.precio)}</span>
                  {puedeGestionar && it.estadoCocina === "pendiente" && !it.pagado && (
                    <button type="button" className="text-red-500 hover:text-red-700" onClick={() => quitar(it.id, `Quitar ${it.cantidad}× ${it.producto} — ${comanda.numero}`)}>
                      <Icon name="trash" className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function SelectorCuenta({ comanda, item, onCambio, setError, setAvisoOffline }: {
  comanda: Comanda; item: ItemComanda; onCambio: () => void;
  setError: (m: string | null) => void; setAvisoOffline: (m: string | null) => void;
}) {
  const [editando, setEditando] = useState(false);
  const [cuenta, setCuenta] = useState(item.cuenta || 1);
  const [enviando, setEnviando] = useState(false);

  const mover = async () => {
    if (cuenta === (item.cuenta || 1)) { setEditando(false); return; }
    setEnviando(true);
    setError(null);
    setAvisoOffline(null);
    try {
      const r = await moverCuentaOffline(comanda.id, item.id, cuenta, `Pasar ${item.producto} a cuenta ${cuenta} — ${comanda.numero}`);
      if (r.offline) {
        setAvisoOffline("Sin conexión: el cambio de cuenta se aplicará al sincronizar.");
      }
      onCambio();
      setEditando(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo pasar el ítem de cuenta.");
    } finally {
      setEnviando(false);
    }
  };

  if (!editando) {
    return <button type="button" className="text-[10px] text-accent-dark font-semibold underline" onClick={() => setEditando(true)}>Pasar</button>;
  }
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-[10px] text-textmuted">Cuenta</span>
      <input
        type="number" min={1} value={cuenta} disabled={enviando}
        className="w-10 rounded border border-borderc text-[10px] px-1 py-0.5"
        onChange={(e) => setCuenta(Math.max(1, Number(e.target.value) || 1))}
      />
      <button type="button" className="text-emerald-600" disabled={enviando} onClick={mover}><Icon name="check" className="h-3 w-3" /></button>
      <button type="button" className="text-textmuted" disabled={enviando} onClick={() => setEditando(false)}><Icon name="x" className="h-3 w-3" /></button>
    </span>
  );
}

function FormAgregarItem({ comandaId, productos, onAgregado, setComanda, setError, setAvisoOffline }: {
  comandaId: number; productos: ProductoDisponible[]; onAgregado: () => void; setComanda: React.Dispatch<React.SetStateAction<Comanda | undefined>>;
  setError: (m: string | null) => void; setAvisoOffline: (m: string | null) => void;
}) {
  const [productoId, setProductoId] = useState<number | "">("");
  const [cantidad, setCantidad] = useState<number>(1);
  const [precio, setPrecio] = useState<number>(0);
  const [observaciones, setObservaciones] = useState("");
  const [chips, setChips] = useState<string[]>([]);
  const [enviando, setEnviando] = useState(false);

  const seleccionar = (id: number | "") => {
    setProductoId(id);
    const p = productos.find((pr) => pr.id === id);
    setPrecio(p?.precioSugerido || 0);
  };

  const alternarChip = (valor: string) => {
    setChips((prev) => (prev.includes(valor) ? prev.filter((c) => c !== valor) : [...prev, valor]));
  };

  const agregar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!productoId) return;
    const producto = productos.find((p) => p.id === productoId);
    setEnviando(true);
    setError(null);
    setAvisoOffline(null);
    try {
      const body = { inventarioPtId: productoId, cantidad, precio, observaciones, chips };
      const r = await agregarItemOffline(comandaId, body, `Agregar ${cantidad}× ${producto?.producto || "producto"}`);
      if (r.offline) {
        setAvisoOffline("Sin conexión: el ítem se agregará al sincronizar. Se muestra ya en la comanda de forma provisional.");
        setComanda((prev) => {
          if (!prev) return prev;
          const itemProvisional: ItemComanda = {
            id: -Date.now(), inventarioPtId: productoId as number, producto: producto?.producto || "Producto",
            cantidad, precio, observaciones: observaciones || null, estadoCocina: "pendiente", creadoEn: new Date().toISOString(),
            chips, enviadoCocina: false, cuenta: 1, pagado: false,
          };
          const items = [...(prev.items || []), itemProvisional];
          return { ...prev, items, total: items.reduce((s, it) => s + it.cantidad * it.precio, 0) };
        });
      } else {
        onAgregado();
      }
      setProductoId("");
      setCantidad(1);
      setPrecio(0);
      setObservaciones("");
      setChips([]);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo agregar el ítem.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <form onSubmit={agregar} className="rounded-lg border border-dashed border-borderc p-3 space-y-2">
      <select className="input-field text-xs" value={productoId} onChange={(e) => seleccionar(e.target.value ? Number(e.target.value) : "")}>
        <option value="">Agregar plato/producto...</option>
        {productos.map((p) => <option key={p.id} value={p.id} disabled={p.cantidadDisponible <= 0}>{p.producto} ({p.cantidadDisponible} disp.)</option>)}
      </select>
      {productoId !== "" && (
        <>
          <div className="flex gap-2">
            <input className="input-field text-xs flex-1" type="number" min={0.01} step="0.01" value={cantidad} onChange={(e) => setCantidad(Number(e.target.value))} placeholder="Cantidad" />
            <input className="input-field text-xs flex-1" type="number" min={0} step="100" value={precio} onChange={(e) => setPrecio(Number(e.target.value))} placeholder="Precio" />
          </div>
          <input className="input-field text-xs" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} placeholder="Observaciones (término, sin cebolla...)" />
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-textmuted mb-1">Preparación</div>
            <div className="flex flex-wrap gap-1">
              {CHIPS_PREPARACION.map((c) => (
                <button
                  key={c.valor} type="button" onClick={() => alternarChip(c.valor)}
                  className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors ${chips.includes(c.valor) ? "border-accent bg-accent-light text-accent-dark" : "border-borderc text-slate-600 bg-white hover:border-slate-300"}`}
                >
                  {c.etiqueta}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-textmuted mb-1">Orden</div>
            <div className="flex flex-wrap gap-1">
              {CHIPS_ORDEN.map((c) => (
                <button
                  key={c.valor} type="button" onClick={() => alternarChip(c.valor)}
                  className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors ${chips.includes(c.valor) ? "border-accent bg-accent-light text-accent-dark" : "border-borderc text-slate-600 bg-white hover:border-slate-300"}`}
                >
                  {c.etiqueta}
                </button>
              ))}
            </div>
          </div>
          <button type="submit" className="btn-secondary w-full text-xs" disabled={enviando}>{enviando ? "Agregando..." : "Agregar a la comanda"}</button>
        </>
      )}
    </form>
  );
}

function FormCobrarComanda({ comandaId, cuenta, esUltimaCuentaPendiente, onCobrado, setError, setAvisoOffline }: {
  comandaId: number; cuenta?: number; esUltimaCuentaPendiente: boolean;
  onCobrado: (cerrada: boolean) => void; setError: (m: string | null) => void; setAvisoOffline: (m: string | null) => void;
}) {
  const [turnos, setTurnos] = useState<Turno[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [turnoCajaId, setTurnoCajaId] = useState<number | "">("");
  const [clienteId, setClienteId] = useState<number | "">("");
  const [medioPago, setMedioPago] = useState("efectivo");
  const [correo, setCorreo] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    api.get("/pos/turnos/abiertos")
      .then((ts: Turno[]) => { setTurnos(ts); guardarCacheTurnos(ts); if (ts.length === 1) setTurnoCajaId(ts[0].id); })
      .catch(() => { leerCacheTurnos().then((ts) => { setTurnos(ts); if (ts.length === 1) setTurnoCajaId(ts[0].id); }); });
    api.get("/pos/clientes")
      .then((cs) => { setClientes(cs); guardarCacheClientes(cs); })
      .catch(() => { leerCacheClientes().then(setClientes); });
  }, []);

  const cobrar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!turnoCajaId) {
      setError(`Seleccione el turno de caja que va a cobrar ${cuenta ? `la cuenta ${cuenta}` : "esta comanda"}.`);
      return;
    }
    setEnviando(true);
    setError(null);
    setAvisoOffline(null);
    try {
      const body = { turnoCajaId, clienteId: clienteId || null, medioPago, correo: correo || null };
      const descripcion = cuenta
        ? `Cobrar cuenta ${cuenta} de la comanda #${comandaId} (${medioPago})`
        : `Cobrar comanda #${comandaId} (${medioPago})`;
      const r = cuenta
        ? await cobrarCuentaOffline(comandaId, cuenta, body, descripcion)
        : await cerrarComandaOffline(comandaId, body, descripcion);
      if (r.offline) {
        setAvisoOffline(
          esUltimaCuentaPendiente
            ? "Sin conexión: el cobro quedó pendiente y se registrará automáticamente al reconectar. La mesa se liberará en ese momento."
            : "Sin conexión: el cobro de esta cuenta quedó pendiente y se registrará al reconectar."
        );
        onCobrado(esUltimaCuentaPendiente);
      } else {
        onCobrado(!!(r as any).data?.comandaCerrada);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo registrar el cobro.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <form onSubmit={cobrar} className="rounded-lg border border-accent/30 bg-accent-light/30 p-3 space-y-2.5">
      {cuenta && <p className="text-xs font-semibold text-accent-dark">Cobrando la cuenta {cuenta}</p>}
      <div>
        <label className="block text-[11px] font-semibold text-slate-600 mb-1">Turno de caja *</label>
        <select className="input-field text-xs" value={turnoCajaId} onChange={(e) => setTurnoCajaId(e.target.value ? Number(e.target.value) : "")} required>
          <option value="">Seleccione la caja que cobra...</option>
          {turnos.map((t) => <option key={t.id} value={t.id}>{t.caja}</option>)}
        </select>
        {turnos.length === 0 && <p className="text-[10px] text-amber-700 mt-1">No hay turnos de caja abiertos — abra uno en POS antes de cobrar.</p>}
      </div>
      <div>
        <label className="block text-[11px] font-semibold text-slate-600 mb-1">Cliente</label>
        <select className="input-field text-xs" value={clienteId} onChange={(e) => setClienteId(e.target.value ? Number(e.target.value) : "")}>
          <option value="">Consumidor final</option>
          {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-[11px] font-semibold text-slate-600 mb-1">Medio de pago</label>
        <div className="grid grid-cols-3 gap-1.5">
          {(["efectivo", "tarjeta", "transferencia"] as const).map((m) => (
            <button
              key={m} type="button" onClick={() => setMedioPago(m)}
              className={`rounded-lg border py-1.5 text-[11px] font-semibold transition-colors ${medioPago === m ? "border-accent bg-white text-accent-dark" : "border-borderc text-slate-600 hover:border-slate-300 bg-white"}`}
            >
              {MEDIO_PAGO_LABEL[m]}
            </button>
          ))}
        </div>
      </div>
      <input className="input-field text-xs" type="email" value={correo} onChange={(e) => setCorreo(e.target.value)} placeholder="Correo para el comprobante (opcional)" />
      <button type="submit" className="btn-primary w-full" disabled={enviando || turnos.length === 0}>
        {enviando ? "Cobrando..." : cuenta ? `Confirmar cobro — cuenta ${cuenta}` : "Confirmar cobro"}
      </button>
    </form>
  );
}

// ------------------------------------------------------------- vista ingredientes
function VistaIngredientes({ puedeRecetas, onCambioStock }: { puedeRecetas: boolean; onCambioStock: () => void }) {
  const [tab, setTab] = useState<"ingredientes" | "recetas">("ingredientes");

  if (!puedeRecetas) {
    return <div className="card p-10 text-center text-sm text-textmuted">No tiene permiso para gestionar ingredientes y recetas.</div>;
  }

  return (
    <div>
      <div className="inline-flex rounded-lg border border-borderc bg-white p-1 mb-4">
        {(["ingredientes", "recetas"] as const).map((t) => (
          <button
            key={t} type="button" onClick={() => setTab(t)}
            className={`px-3.5 py-1.5 rounded-md text-xs font-semibold capitalize transition-colors ${tab === t ? "bg-accent text-white" : "text-slate-600 hover:bg-surface"}`}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === "ingredientes" ? <PanelIngredientes onCambioStock={onCambioStock} /> : <PanelRecetas />}
    </div>
  );
}

function PanelIngredientes({ onCambioStock }: { onCambioStock: () => void }) {
  const [ingredientes, setIngredientes] = useState<Ingrediente[] | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [modalNuevo, setModalNuevo] = useState(false);
  const [editar, setEditar] = useState<Ingrediente | null>(null);
  const [ajustar, setAjustar] = useState<Ingrediente | null>(null);

  const cargar = async () => {
    try {
      setIngredientes(await api.get("/restaurante/ingredientes"));
      onCambioStock();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudieron cargar los ingredientes.");
    }
  };
  useEffect(() => { cargar(); }, []);

  const eliminar = async (id: number) => {
    setError(null);
    try {
      await api.del(`/restaurante/ingredientes/${id}`);
      cargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo eliminar el ingrediente (puede estar en uso en una receta).");
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button type="button" className="btn-primary flex items-center gap-1.5" onClick={() => setModalNuevo(true)}>
          <Icon name="plus" className="h-4 w-4" /> Nuevo ingrediente
        </button>
      </div>
      {error && <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
      {ingredientes === undefined ? (
        <div className="card p-10 text-center text-sm text-textmuted">Cargando...</div>
      ) : ingredientes.length === 0 ? (
        <div className="card p-10 text-center text-sm text-textmuted">No hay ingredientes registrados todavía.</div>
      ) : (
        <div className="overflow-x-auto card">
          <table className="w-full text-xs">
            <thead className="bg-surface text-textmuted">
              <tr>
                <th className="text-left px-3 py-2">Ingrediente</th>
                <th className="text-right px-3 py-2">Disponible</th>
                <th className="text-right px-3 py-2">Stock mínimo</th>
                <th className="text-right px-3 py-2">Costo unitario</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {ingredientes.map((i) => (
                <tr key={i.id} className={`border-t border-borderc ${i.bajoStock ? "bg-amber-50" : ""}`}>
                  <td className="px-3 py-2 font-medium text-slate-700">
                    {i.nombre} {i.bajoStock && <Icon name="alertTriangle" className="h-3.5 w-3.5 inline text-amber-600 ml-1" />}
                  </td>
                  <td className="px-3 py-2 text-right">{i.cantidadDisponible} {i.unidadMedida === "unidad" ? "u." : i.unidadMedida === "gramos" ? "g" : "ml"}</td>
                  <td className="px-3 py-2 text-right">{i.stockMinimo}</td>
                  <td className="px-3 py-2 text-right">{money(i.costoUnitario)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button type="button" className="text-accent-dark font-semibold mr-2" onClick={() => setAjustar(i)}>Ajustar</button>
                    <button type="button" className="text-slate-400 hover:text-slate-700 mr-2" onClick={() => setEditar(i)}><Icon name="settings" className="h-3.5 w-3.5 inline" /></button>
                    <button type="button" className="text-red-500 hover:text-red-700" onClick={() => eliminar(i.id)}><Icon name="trash" className="h-3.5 w-3.5 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {modalNuevo && <ModalIngrediente onClose={() => setModalNuevo(false)} onGuardado={() => { setModalNuevo(false); cargar(); }} />}
      {editar && <ModalIngrediente ingrediente={editar} onClose={() => setEditar(null)} onGuardado={() => { setEditar(null); cargar(); }} />}
      {ajustar && <ModalAjusteIngrediente ingrediente={ajustar} onClose={() => setAjustar(null)} onGuardado={() => { setAjustar(null); cargar(); }} />}
    </div>
  );
}

function ModalIngrediente({ ingrediente, onClose, onGuardado }: { ingrediente?: Ingrediente; onClose: () => void; onGuardado: () => void }) {
  const [nombre, setNombre] = useState(ingrediente?.nombre || "");
  const [unidadMedida, setUnidadMedida] = useState(ingrediente?.unidadMedida || "gramos");
  const [cantidadDisponible, setCantidadDisponible] = useState<number>(ingrediente?.cantidadDisponible ?? 0);
  const [stockMinimo, setStockMinimo] = useState<number>(ingrediente?.stockMinimo ?? 0);
  const [costoUnitario, setCostoUnitario] = useState<number>(ingrediente?.costoUnitario ?? 0);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      if (ingrediente) {
        await api.put(`/restaurante/ingredientes/${ingrediente.id}`, { nombre, unidadMedida, stockMinimo, costoUnitario });
      } else {
        await api.post("/restaurante/ingredientes", { nombre, unidadMedida, cantidadDisponible, stockMinimo, costoUnitario });
      }
      onGuardado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo guardar el ingrediente.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={guardar} className="card w-full max-w-sm p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800">{ingrediente ? "Editar ingrediente" : "Nuevo ingrediente"}</h3>
          <button type="button" onClick={onClose} className="text-textmuted hover:text-slate-700"><Icon name="x" className="h-4 w-4" /></button>
        </div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Nombre *</label>
          <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required autoFocus /></div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Unidad de medida</label>
          <select className="input-field" value={unidadMedida} onChange={(e) => setUnidadMedida(e.target.value)}>
            {UNIDADES_INGREDIENTE.map((u) => <option key={u} value={u}>{u}</option>)}
          </select></div>
        {!ingrediente && (
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Cantidad inicial disponible</label>
            <input className="input-field" type="number" min={0} step="0.01" value={cantidadDisponible} onChange={(e) => setCantidadDisponible(Number(e.target.value))} /></div>
        )}
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Stock mínimo (alerta bajo stock)</label>
          <input className="input-field" type="number" min={0} step="0.01" value={stockMinimo} onChange={(e) => setStockMinimo(Number(e.target.value))} /></div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Costo unitario</label>
          <input className="input-field" type="number" min={0} step="0.01" value={costoUnitario} onChange={(e) => setCostoUnitario(Number(e.target.value))} /></div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Guardando..." : "Guardar"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalAjusteIngrediente({ ingrediente, onClose, onGuardado }: { ingrediente: Ingrediente; onClose: () => void; onGuardado: () => void }) {
  const [cantidad, setCantidad] = useState<number>(0);
  const [tipoMovimiento, setTipoMovimiento] = useState<"entrada" | "ajuste">("entrada");
  const [observaciones, setObservaciones] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post(`/restaurante/ingredientes/${ingrediente.id}/ajuste`, { cantidad, tipoMovimiento, observaciones });
      onGuardado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo ajustar el ingrediente.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={guardar} className="card w-full max-w-sm p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800">Ajustar {ingrediente.nombre}</h3>
          <button type="button" onClick={onClose} className="text-textmuted hover:text-slate-700"><Icon name="x" className="h-4 w-4" /></button>
        </div>
        <p className="text-xs text-textmuted">Disponible actual: {ingrediente.cantidadDisponible} {ingrediente.unidadMedida}</p>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Tipo de movimiento</label>
          <div className="grid grid-cols-2 gap-1.5">
            {(["entrada", "ajuste"] as const).map((t) => (
              <button key={t} type="button" onClick={() => setTipoMovimiento(t)}
                className={`rounded-lg border py-1.5 text-xs font-semibold capitalize ${tipoMovimiento === t ? "border-accent bg-accent-light text-accent-dark" : "border-borderc text-slate-600 bg-white"}`}>
                {t === "entrada" ? "Reposición (+)" : "Ajuste manual (+/-)"}
              </button>
            ))}
          </div></div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">
          {tipoMovimiento === "entrada" ? "Cantidad a sumar" : "Cantidad (positiva o negativa)"}</label>
          <input className="input-field" type="number" step="0.01" value={cantidad} onChange={(e) => setCantidad(Number(e.target.value))} required autoFocus /></div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Observaciones</label>
          <input className="input-field" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} placeholder="Compra de reposición, merma, conteo físico..." /></div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Guardando..." : "Guardar"}</button>
        </div>
      </form>
    </div>
  );
}

function PanelRecetas() {
  const [recetas, setRecetas] = useState<Receta[] | undefined>(undefined);
  const [ingredientes, setIngredientes] = useState<Ingrediente[]>([]);
  const [productos, setProductos] = useState<ProductoDisponible[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editando, setEditando] = useState<{ producto: string; ingredientes: { ingredienteId: number; cantidad: number }[] } | null>(null);

  const cargar = async () => {
    try {
      const [r, i, p] = await Promise.all([
        api.get("/restaurante/recetas"), api.get("/restaurante/ingredientes"), api.get("/pos/productos-disponibles"),
      ]);
      setRecetas(r); setIngredientes(i); setProductos(p);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudieron cargar las recetas.");
    }
  };
  useEffect(() => { cargar(); }, []);

  const eliminar = async (id: number) => {
    setError(null);
    try {
      await api.del(`/restaurante/recetas/${id}`);
      cargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo eliminar la receta.");
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button type="button" className="btn-primary flex items-center gap-1.5" onClick={() => setEditando({ producto: "", ingredientes: [] })}>
          <Icon name="plus" className="h-4 w-4" /> Nueva/editar receta
        </button>
      </div>
      {error && <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
      {recetas === undefined ? (
        <div className="card p-10 text-center text-sm text-textmuted">Cargando...</div>
      ) : recetas.length === 0 ? (
        <div className="card p-10 text-center text-sm text-textmuted">
          No hay fichas técnicas configuradas. Sin una receta, vender ese plato no descuenta ingredientes (solo el producto terminado).
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {recetas.map((r) => (
            <div key={r.id} className="card p-4">
              <div className="flex items-start justify-between mb-2">
                <span className="font-display font-bold text-sm">{r.producto}</span>
                <div className="flex items-center gap-2">
                  <button type="button" className="text-accent-dark font-semibold text-xs" onClick={() => setEditando({ producto: r.producto, ingredientes: r.ingredientes.map((i) => ({ ingredienteId: i.ingredienteId, cantidad: i.cantidad })) })}>Editar</button>
                  <button type="button" className="text-red-500" onClick={() => eliminar(r.id)}><Icon name="trash" className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <ul className="text-xs text-slate-600 space-y-0.5">
                {r.ingredientes.map((i) => (
                  <li key={i.id}>{i.cantidad} {i.unidadMedida === "unidad" ? "u." : i.unidadMedida === "gramos" ? "g" : "ml"} — {i.ingredienteNombre}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      {editando && (
        <ModalReceta
          inicial={editando} productos={productos} ingredientes={ingredientes}
          onClose={() => setEditando(null)} onGuardado={() => { setEditando(null); cargar(); }}
        />
      )}
    </div>
  );
}

function ModalReceta({ inicial, productos, ingredientes, onClose, onGuardado }: {
  inicial: { producto: string; ingredientes: { ingredienteId: number; cantidad: number }[] };
  productos: ProductoDisponible[]; ingredientes: Ingrediente[]; onClose: () => void; onGuardado: () => void;
}) {
  const [producto, setProducto] = useState(inicial.producto);
  const [filas, setFilas] = useState(inicial.ingredientes.length ? inicial.ingredientes : [{ ingredienteId: 0, cantidad: 0 }]);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const actualizarFila = (i: number, campo: "ingredienteId" | "cantidad", valor: number) => {
    setFilas((prev) => prev.map((f, idx) => (idx === i ? { ...f, [campo]: valor } : f)));
  };
  const agregarFila = () => setFilas((prev) => [...prev, { ingredienteId: 0, cantidad: 0 }]);
  const quitarFila = (i: number) => setFilas((prev) => prev.filter((_, idx) => idx !== i));

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    const validas = filas.filter((f) => f.ingredienteId && f.cantidad > 0);
    if (!producto) { setError("Seleccione el producto del menú."); return; }
    if (validas.length === 0) { setError("Agregue al menos un ingrediente con cantidad mayor a cero."); return; }
    setEnviando(true);
    setError(null);
    try {
      await api.put("/restaurante/recetas", { producto, ingredientes: validas });
      onGuardado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo guardar la receta.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-6">
      <form onSubmit={guardar} className="card w-full max-w-md max-h-full overflow-y-auto p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800">Ficha técnica del plato</h3>
          <button type="button" onClick={onClose} className="text-textmuted hover:text-slate-700"><Icon name="x" className="h-4 w-4" /></button>
        </div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Producto del menú *</label>
          <select className="input-field" value={producto} onChange={(e) => setProducto(e.target.value)} required>
            <option value="">Seleccione...</option>
            {productos.map((p) => <option key={p.id} value={p.producto}>{p.producto}</option>)}
          </select></div>
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-slate-600">Ingredientes que consume (por unidad vendida)</label>
          {filas.map((f, i) => (
            <div key={i} className="flex gap-2 items-center">
              <select className="input-field text-xs flex-1" value={f.ingredienteId || ""} onChange={(e) => actualizarFila(i, "ingredienteId", Number(e.target.value))}>
                <option value="">Ingrediente...</option>
                {ingredientes.map((ing) => <option key={ing.id} value={ing.id}>{ing.nombre} ({ing.unidadMedida})</option>)}
              </select>
              <input className="input-field text-xs w-24" type="number" min={0} step="0.01" placeholder="Cant." value={f.cantidad || ""} onChange={(e) => actualizarFila(i, "cantidad", Number(e.target.value))} />
              {filas.length > 1 && <button type="button" className="text-red-500" onClick={() => quitarFila(i)}><Icon name="trash" className="h-3.5 w-3.5" /></button>}
            </div>
          ))}
          <button type="button" className="text-xs text-accent-dark font-semibold" onClick={agregarFila}>+ Agregar ingrediente</button>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Guardando..." : "Guardar receta"}</button>
        </div>
      </form>
    </div>
  );
}
